import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/Table';
import { Check, Flag } from 'lucide-react';
import { TriangleUp, TriangleDown, TriangleBoth, FunnelIcon } from '../ui/SortIcons';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/Badge';
import { formatSlashDate } from '../../utils/dateUtils';
import { phiMask, isPhiMasked } from '../../utils/phiUtils';
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../ui/DropdownMenu';

const optionsMap = {
  client_id: 'clients',
  tpa_id: 'tpa_carriers',
  referral_type: 'referral_types',
};

const statusColorMap = {
  acknowledge:               'bg-status-acknowledge/15 text-status-acknowledge',
  'acknowledge referral':    'bg-status-acknowledge/15 text-status-acknowledge',
  acknowledge_referral:      'bg-status-acknowledge/15 text-status-acknowledge',
  initial:                   'bg-status-initial/15 text-status-initial',
  'provide initial decision':'bg-status-initial/15 text-status-initial',
  provide_initial_decision:  'bg-status-initial/15 text-status-initial',
  review:                    'bg-status-review/15 text-status-review',
  in_review:                 'bg-status-review/15 text-status-review',
  escalate:                  'bg-status-escalate/15 text-status-escalate',
  escalated:                 'bg-status-escalate/15 text-status-escalate',
  manage:                    'bg-status-manage/15 text-status-manage',
  in_management:             'bg-status-manage/15 text-status-manage',
  closed:                    'bg-status-closed/15 text-status-closed',
};

function StatusBadge({ value }) {
  if (!value) return null;
  const key = value.toLowerCase();
  const colors = statusColorMap[key];
  if (!colors) return <span>{value}</span>;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors}`}>
      {value}
    </span>
  );
}

function renderLastNameCell(val, row) {
  if (!row?.last_name_supported) return '';
  if (isPhiMasked('last_name', row?.phi_masked_fields)) {
    return phiMask('last_name');
  }
  return val ?? '';
}

const DEFAULT_SORT = [
  { field: 'impact', direction: 'desc' },
  { field: 'due_date', direction: 'asc' },
];

const getReferralSelectionId = (referral) => {
  const id = referral?.referral_id ?? referral?.id;
  return id === null || id === undefined ? null : String(id);
};

const REFERRAL_COLUMNS = [
  { key: 'client', label: 'Client', displayKey: 'client', sortable: true, filterable: true, filterKey: 'client_id', textSize: 'text-sm', className: 'whitespace-nowrap' },
  { key: 'tpa_carrier', label: 'TPA/Carrier', displayKey: 'tpa_carrier', sortable: true, filterable: true, filterKey: 'tpa_id', textSize: 'text-sm', hideForOrgTypes: ['TPA', 'CARRIER'] },
  { key: 'referral_type', label: 'Referral Type', displayKey: 'referral_type', sortable: true, filterable: true, filterKey: 'referral_type', render: (val) => val ? <Badge variant="outline" className="rounded px-2 py-0 font-medium whitespace-nowrap">{val}</Badge> : '' },
  { key: 'referral_date', label: 'Published Date', displayKey: 'publish_dt', sortable: true, className: 'text-muted-foreground whitespace-nowrap', render: (val) => val ? formatSlashDate(val) : '' },
  { key: 'referral_age', label: 'Referral Age', displayKey: 'referral_age', sortable: true, className: 'text-muted-foreground whitespace-nowrap', render: (val) => val !== null && val !== undefined ? `${val} days` : '', internalOnly: true },
  { key: 'aging_out', label: 'Aging Out', displayKey: 'aging_out', sortable: false, render: (val) => val ? <Flag className="h-3.5 w-3.5 text-destructive mx-auto" /> : '', internalOnly: true },
  { key: 'referral_id', label: 'Referral ID', displayKey: 'referral_id', sortable: true, textSize: 'text-sm', className: 'font-medium whitespace-nowrap' },
  { key: 'referral_name', label: 'Referral Name', displayKey: 'referral_name', sortable: true, truncate: true, className: 'max-w-[180px]' },
  { key: 'last_name', label: 'Last Name', displayKey: 'last_name', sortable: false, truncate: true, className: 'max-w-[140px] whitespace-nowrap', render: (val, row) => renderLastNameCell(val, row) },
  { key: 'status_display', label: 'Status', displayKey: 'tpa_carrier_status', sortable: true, className: 'whitespace-nowrap', render: (val) => <StatusBadge value={val} /> },
  { key: 'reason_display', label: 'Reason', displayKey: 'tpa_carrier_reason', sortable: true, truncate: true, className: 'max-w-[130px] text-muted-foreground' },
  { key: 'latest_comment', label: 'Latest Comment', displayKey: 'latest_comment', sortable: false, truncate: true, className: 'max-w-[180px] text-muted-foreground' },
  { key: 'assigned_to', label: 'Assigned To', displayKey: 'assigned_to', sortable: true, render: (val) => val ? <Badge variant="outline" className="rounded-sm px-3 py-0.5 font-medium whitespace-nowrap">{val}</Badge> : '' },
  { key: 'assigned_action', label: 'Assigned Action', displayKey: 'assigned_action', sortable: true, render: (val) => val ? <Badge variant="outline" className="min-w-[90px] px-2 py-0 font-medium whitespace-nowrap">{val}</Badge> : '' },
  { key: 'due_date', label: 'Due Date', displayKey: 'due_date', sortable: true, render: (val) => {
    if (!val) return '';
    const dueDate = new Date(val);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isOverdue = dueDate < today;
    return <span className={`whitespace-nowrap ${isOverdue ? 'font-medium text-destructive' : 'text-muted-foreground'}`}>{formatSlashDate(val)}</span>;
  } },
  { key: 'impact', label: 'Impact', displayKey: 'impact_tier', sortable: true, render: (tier) => {
    if (!tier) return '';
    return (
      <span className="flex gap-0.5">
        {[1,2,3,4,5].map(i => (
          <span key={i} className={`inline-block w-1.5 h-1.5 rounded-full ${i <= tier ? 'bg-foreground' : 'bg-muted'}`} />
        ))}
      </span>
    );
  } },
];

function getVisibleReferralColumns(columns, { isInternal, orgType, displayRules = {} }) {
  return columns.filter(c => {
    if (!isInternal && c.internalOnly) return false;
    if (c.hideForOrgTypes && c.hideForOrgTypes.includes(orgType)) return false;
    if (c.key === 'last_name' && !displayRules?.last_name?.visible) return false;
    return true;
  });
}

function ColumnFilterDropdown({ options, value, onChange, active }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        className={`flex items-center justify-center py-1 px-0.5 rounded transition-colors hover:bg-muted-foreground/10 text-muted-foreground ${active ? 'text-primary' : ''}`}
        aria-label="Filter"
        aria-expanded={open}
        data-testid="column-filter-button"
        onClick={() => setOpen(prev => !prev)}
      >
        <FunnelIcon className={`h-2.5 w-2.5 ${active ? 'opacity-100' : 'opacity-[0.35]'}`} />
      </button>
      {open && (
        <div
          className="filter-dropdown-menu"
          data-testid="column-filter-dropdown"
        >
          <button
            type="button"
            className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-filter-bg text-left"
            onClick={() => { onChange(''); setOpen(false); }}
          >
            <span className="w-4">{value === '' ? <Check className="h-4 w-4" /> : ''}</span>
            <span>All</span>
          </button>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-filter-bg text-left"
              onClick={() => { onChange(option.value); setOpen(false); }}
            >
              <span className="w-4">{value === option.value ? <Check className="h-4 w-4" /> : ''}</span>
              <span className="filter-dropdown-text" title={option.label}>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ReferralTable({
  columns,
  rows = [],
  emptyStateText,
  sortColumns,
  isDefaultSort,
  defaultSort,
  openSortMenu,
  setOpenSortMenu,
  onSort,
  onClearColumnSort,
  filters,
  filterOptions,
  onFilterChange,
  onRowDoubleClick,
  selectedRowId: controlledSelectedRowId,
  onSelectedRowChange,
  stepLabels,
  onWorkflowCounts,
  onFilteredCountChange,
}) {
  // --- Double-click detection refs ---
  // lastClickTime: stores the timestamp of the most recent click per row id
  // lastClickTimer: stores the setTimeout id per row id so we can clearTimeout
  const lastClickTime = useRef({});
  // Emit workflow counts from the *unfiltered* rows whenever they or the step
  // labels change. Counts reflect the full result set so the workflow bar
  // stays independent of the client-side filters applied below.
  const lastClickTimer = useRef({});

  // ← ADDED: unmount cleanup
  useEffect(() => {
    return () => {
      Object.values(lastClickTimer.current).forEach(clearTimeout);
      lastClickTime.current = {};
      lastClickTimer.current = {};
    };
  }, []);
  const [internalSelectedRowId, setInternalSelectedRowId] = useState(null);
  const hasControlledSelection = controlledSelectedRowId !== undefined;
  const selectedRowId = hasControlledSelection ? controlledSelectedRowId : internalSelectedRowId;

  // Keep onRowDoubleClick in a ref so handleRowClick never captures a stale closure
  const onRowDoubleClickRef = useRef(onRowDoubleClick);
  useEffect(() => {
    onRowDoubleClickRef.current = onRowDoubleClick;
  }, [onRowDoubleClick]);

  const setSelectedRowId = useCallback((id) => {
    if (hasControlledSelection) {
      onSelectedRowChange?.(id);
      return;
    }
    setInternalSelectedRowId(id);
  }, [hasControlledSelection, onSelectedRowChange]);

  const handleRowClick = useCallback((referral) => {
    const id = getReferralSelectionId(referral);
    const now = Date.now();

    if (lastClickTime.current[id] && now - lastClickTime.current[id] < 500) {
      //Double-click detected:
      // 1. Cancel the pending cleanup timer so it can't interfere
      clearTimeout(lastClickTimer.current[id]);
      // 2. Wipe state for this row immediately
      delete lastClickTime.current[id];
      delete lastClickTimer.current[id];
      // 3. Fire the double-click handler via ref (always fresh, never stale)
      setSelectedRowId(id);
      onRowDoubleClickRef.current(referral);
      return;
    }

    // Single click — reset the entire map so stale entries from other rows
    // can never accidentally match on a cross-row double-click sequence.
    // Cancel any existing timer for this row first.
    if (lastClickTimer.current[id]) {
      clearTimeout(lastClickTimer.current[id]);
    }
    lastClickTime.current = { [id]: now };
    lastClickTimer.current = {};

    setSelectedRowId(id);

    // Schedule cleanup; clearTimeout above ensures only one timer runs at a time.
    lastClickTimer.current[id] = setTimeout(() => {
      delete lastClickTime.current[id];
      delete lastClickTimer.current[id];
    }, 500);

  }, [setSelectedRowId]); // onRowDoubleClick intentionally omitted — used via ref

  useEffect(() => {
    if (!onWorkflowCounts) return;
    const steps = stepLabels || [];
    const stepKeys = steps.map((s) => s.key);
    const counts = Object.fromEntries(stepKeys.map((k) => [k, 0]));
    const overdueCounts = Object.fromEntries(stepKeys.map((k) => [k, 0]));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    rows.forEach((r) => {
      if (r.workflow_step in counts) {
        counts[r.workflow_step]++;
        if (r.due_date && new Date(r.due_date) < today) {
          overdueCounts[r.workflow_step]++;
        }
      }
    });
    onWorkflowCounts({
      counts,
      overdueCounts,
      totalReferrals: rows.length,
    });
  }, [rows, stepLabels, onWorkflowCounts]);

  // Apply client-side workflow-step and assigned-to filters.
  // All filtering is server-side; displayRows is the full page as returned.
  const displayRows = rows;

  // Emit filtered count whenever displayRows changes
  useEffect(() => {
    if (onFilteredCountChange) {
      onFilteredCountChange(displayRows.length);
    }
  }, [displayRows, onFilteredCountChange]);

  // Keep the selected row through refetches/reorders, but drop it once the
  // selected referral is no longer visible in the current table result.
  useEffect(() => {
    if (selectedRowId !== null) {
      const isStillVisible = displayRows.some((r) => getReferralSelectionId(r) === selectedRowId);
      if (!isStillVisible) {
        setSelectedRowId(null);
      }
    }
  }, [displayRows, selectedRowId, setSelectedRowId]);

  const getSortIcon = (columnKey) => {
    const activeSort = sortColumns.find((s) => s.field === columnKey);
    const defaultSortEntry = isDefaultSort ? defaultSort.find((s) => s.field === columnKey) : null;
    const sortEntry = activeSort || defaultSortEntry;

    if (!sortEntry) {
      return <TriangleBoth className="h-3 w-3 opacity-[0.35]" />;
    }

    const cls = 'h-3 w-3';
    if (sortEntry.direction === 'asc') {
      return <TriangleUp className={cls} />;
    }

    return <TriangleDown className={cls} />;
  };

  return (
    <Table className="min-w-[1400px]" wrapperClassName="h-full">
      <TableHeader>
        <TableRow className="bg-muted">
          {columns.map((column) => (
            <TableHead
              key={column.key}
              className={cn(
                'h-8 px-3 font-semibold text-xs whitespace-nowrap group relative align-middle transition-colors',
                sortColumns.some((s) => s.field === column.key) ||
                  (isDefaultSort && defaultSort.some((s) => s.field === column.key)) ||
                  (column.filterKey && filters[column.filterKey])
                  ? 'bg-filter-bg-active text-filter-text-active'
                  : 'text-foreground hover:bg-muted'
              )}
            >
              <div className={column.filterable ? 'flex items-center' : 'flex items-center gap-1.5'}>
                {column.filterable && <span className="mr-2">{column.label}</span>}
                {!column.filterable && column.label}
                {column.filterable && (
                  <ColumnFilterDropdown
                    options={filterOptions[optionsMap[column.filterKey]]}
                    value={filters[column.filterKey]}
                    onChange={(val) => onFilterChange(column.filterKey, val)}
                    active={!!filters[column.filterKey]}
                  />
                )}
                {column.sortable !== false && (
                  <>
                    <button
                      className="cursor-pointer hover:bg-muted-foreground/10 rounded py-1 px-0.5 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenSortMenu(openSortMenu === column.key ? null : column.key);
                      }}
                    >
                      {getSortIcon(column.key)}
                    </button>
                    {openSortMenu === column.key && (() => {
                      const activeSort = sortColumns.find((s) => s.field === column.key);
                      const defaultSortEntry = isDefaultSort ? defaultSort.find((s) => s.field === column.key) : null;
                      const currentDir = (activeSort || defaultSortEntry)?.direction;

                      const showClear = !!activeSort || (isDefaultSort && !!defaultSortEntry);

                      return (
                        <DropdownMenuContent
                          open={true}
                          onClose={() => setOpenSortMenu(null)}
                          align="end"
                          className="min-w-[140px]"
                        >
                          <DropdownMenuItem
                            onClick={() => onSort(column.key, 'asc')}
                            className={String(currentDir) === 'asc' ? 'bg-accent text-accent-foreground font-semibold' : ''}
                          >
                            <TriangleUp className="h-4 w-4 mr-2" />
                            Ascending
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => onSort(column.key, 'desc')}
                            className={String(currentDir) === 'desc' ? 'bg-accent text-accent-foreground font-semibold' : ''}
                          >
                            <TriangleDown className="h-4 w-4 mr-2" />
                            Descending
                          </DropdownMenuItem>
                          {showClear && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => onClearColumnSort(column.key)}>
                                <span className="mr-2">×</span>
                                Clear sort
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      );
                    })()}
                  </>
                )}
              </div>
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {displayRows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columns.length} className="h-32 text-center align-middle">
              <p className="text-muted-foreground" data-testid="referral-empty-state">
                {emptyStateText || 'No referrals found'}
              </p>
            </TableCell>
          </TableRow>
        ) : (
          displayRows.map((referral) => {
            const rowId = getReferralSelectionId(referral);
            return (
              <TableRow
                key={rowId}
                data-state={selectedRowId === rowId ? 'selected' : undefined}
                className="referral-table-row hover:bg-muted/30 cursor-pointer select-none"
                onClick={() => handleRowClick(referral)}
                title="Double-click to view details"
              >
                {columns.map((column) => (
                  <TableCell key={column.key} data-testid={`cell-${column.key}`} className={cn('px-3 py-1 text-foreground text-left align-middle', column.textSize || 'text-xs', column.className)}>
                    <div className="flex items-center min-w-0">
                      {(() => {
                        const fieldName = column.displayKey || column.key;
                        const val = referral[fieldName];
                        if (column.truncate) {
                          return (
                            <span className="truncate" title={val || ''}>
                              {column.render ? column.render(val, referral) : (val ?? '')}
                            </span>
                          );
                        }
                        return column.render ? column.render(val, referral) : (val ?? '');
                      })()}
                    </div>
                  </TableCell>
                ))}
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}

export { ReferralTable, DEFAULT_SORT, REFERRAL_COLUMNS, getVisibleReferralColumns, getReferralSelectionId };
